// usePasskeys.ts — U65 (locker-pwa-passkey Phase 2.5)
// 패스키 목록 조회 + 뮤테이션(등록/인증/이름변경/삭제) TanStack Query 훅.
//
// queryKey 규칙:
//   ['passkeys']  — 목록 조회 + 뮤테이션 후 무효화

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  passkeyApi,
  registerPasskeyFlow,
  authenticatePasskeyFlow,
  type PasskeyListResponse,
  type PasskeyRegisterFinishResponse,
  type PasskeyAuthFinishResponse,
  type PasskeySummary,
} from '../api/passkey';
import { isApiError } from '../api/client';

// ─── queryKey 상수 ───────────────────────────────────────────

/** 패스키 캐시 키 — 목록 및 뮤테이션 무효화에 공유 사용 */
export const PASSKEYS_KEY = ['passkeys'] as const;

// ─── 조회 훅 ─────────────────────────────────────────────────

/**
 * 등록된 패스키 목록 조회 훅.
 *
 * - 성공 시 PasskeyListResponse({ items: PasskeySummary[] }) 반환
 * - 401은 api/client.ts의 onUnauthorized 핸들러가 처리
 */
export function usePasskeys() {
  return useQuery<PasskeyListResponse>({
    queryKey: PASSKEYS_KEY,
    queryFn: () => passkeyApi.list(),
    // 서버에서 패스키가 꺼져 있으면(503 passkey_disabled) 재시도 무의미
    retry: (count, err) =>
      !(isApiError(err) && err.error === 'passkey_disabled') && count < 3,
  });
}

// ─── 뮤테이션 훅 ─────────────────────────────────────────────

/**
 * 패스키 뮤테이션 훅 모음.
 *
 * - registerPasskey: deviceLabel을 받아 전체 등록 흐름 수행 (begin → WebAuthn → finish)
 * - authenticatePasskey: 전체 인증 흐름 수행 (begin → WebAuthn → finish)
 * - renamePasskey: 기기 이름 변경 (id + label)
 * - removePasskey: 패스키 삭제 (id)
 *
 * 모든 뮤테이션은 성공 시 ['passkeys'] 캐시를 무효화한다.
 */
export function usePasskeyMutations() {
  const qc = useQueryClient();

  /** passkeys 목록 캐시 무효화 헬퍼 */
  const invalidatePasskeys = () =>
    qc.invalidateQueries({ queryKey: PASSKEYS_KEY });

  /**
   * 패스키 등록.
   * mutationFn 인수: deviceLabel (기기 이름 문자열)
   * 반환: PasskeyRegisterFinishResponse { id, credentialId }
   */
  const registerPasskey = useMutation<
    PasskeyRegisterFinishResponse,
    unknown,
    string
  >({
    mutationFn: (deviceLabel: string) => registerPasskeyFlow(deviceLabel),
    onSuccess: () => {
      invalidatePasskeys();
    },
  });

  /**
   * 패스키 인증 (잠금 해제).
   * mutationFn 인수: 없음 (void)
   * 반환: PasskeyAuthFinishResponse { unlocked: true }
   */
  const authenticatePasskey = useMutation<
    PasskeyAuthFinishResponse,
    unknown,
    void
  >({
    mutationFn: () => authenticatePasskeyFlow(),
    // 인증 후 passkeys 목록 재조회 — lastUsedAt 갱신 반영
    onSuccess: () => {
      invalidatePasskeys();
    },
  });

  /**
   * 패스키 이름 변경.
   * mutationFn 인수: { id: number; label: string }
   */
  const renamePasskey = useMutation<
    PasskeySummary,
    unknown,
    { id: number; label: string }
  >({
    mutationFn: ({ id, label }) => passkeyApi.rename(id, label),
    onSuccess: () => {
      invalidatePasskeys();
    },
  });

  /**
   * 패스키 삭제.
   * mutationFn 인수: id (number)
   */
  const removePasskey = useMutation<void, unknown, number>({
    mutationFn: (id: number) => passkeyApi.remove(id),
    onSuccess: () => {
      invalidatePasskeys();
    },
  });

  return {
    registerPasskey,
    authenticatePasskey,
    renamePasskey,
    removePasskey,
  };
}
